<template>
  <component
    :is="tag"
    :to="!disabled && !external ? to : undefined"
    :href="external ? to : undefined"
    :target="external ? '_blank' : undefined"
    :rel="external ? 'noopener noreferrer' : undefined"
    :class="rootClasses"
    :aria-current="isActive ? 'page' : undefined"
    :aria-disabled="disabled ? 'true' : undefined"
    @click="onClick"
  >
    <span v-if="icon" class="v2-nav-item__icon" aria-hidden="true">
      <i
        :class="['v2-mdi', icon]"
        :style="{ fontSize: iconSize + 'px' }"
      />
    </span>
    <span v-if="!collapsed" class="v2-nav-item__label">{{ label }}</span>
    <span v-if="$slots.suffix && !collapsed" class="v2-nav-item__suffix">
      <slot name="suffix" />
    </span>
  </component>
</template>

<script>
export default {
  name: "NavItem",
  props: {
    label: { type: String, required: true },
    to: { type: String, default: "" },
    icon: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    external: { type: Boolean, default: false },
    collapsed: { type: Boolean, default: false },
    variant: {
      type: String,
      default: "default",
      validator: (v) => ["default", "navy", "navy-danger"].includes(v),
    },
    activeMatch: {
      type: String,
      default: "exact",
      validator: (v) => ["exact", "prefix", "none"].includes(v),
    },
  },
  computed: {
    tag() {
      if (this.disabled) return "span";
      if (this.external) return "a";
      return "router-link";
    },
    isActive() {
      if (this.activeMatch === "none") return false;
      if (this.disabled) return false;
      if (!this.$route || typeof this.$route.path !== "string") return false;
      const path = this.$route.path;
      if (this.activeMatch === "prefix") {
        return path === this.to || path.startsWith(this.to + "/");
      }
      return path === this.to;
    },
    rootClasses() {
      return [
        "v2-nav-item",
        `v2-nav-item--${this.variant}`,
        {
          "v2-nav-item--active": this.isActive,
          "v2-nav-item--disabled": this.disabled,
          "v2-nav-item--collapsed": this.collapsed,
        },
      ];
    },
    iconSize() {
      if (this.variant === "navy" || this.variant === "navy-danger") {
        return this.collapsed ? 24 : 22;
      }
      return 20;
    },
  },
  methods: {
    onClick(e) {
      if (this.disabled) {
        e.preventDefault();
        return;
      }
      this.$emit("click", e);
    },
  },
};
</script>

<style scoped>
.v2-nav-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-neutral-700);
  font-family: var(--font-body);
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-medium);
  text-decoration: none;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
  user-select: none;
  position: relative;
}

.v2-nav-item:hover {
  background-color: var(--color-neutral-100);
  color: var(--color-text);
}

.v2-nav-item:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-nav-item--active {
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
  font-weight: var(--fw-semibold);
}

.v2-nav-item--active:hover {
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
}

.v2-nav-item--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.v2-nav-item--collapsed {
  justify-content: center;
  padding: var(--space-3);
  width: 48px;
  height: 48px;
}

.v2-nav-item--collapsed .v2-nav-item__icon {
  margin: 0;
}

.v2-nav-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: inherit;
}

.v2-mdi,
.v2-mdi::before {
  color: inherit !important;
  line-height: 1;
  display: inline-block;
  font-family: "Material Design Icons", "MDI", sans-serif;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  speak: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.v2-nav-item__icon :deep(.v-icon),
.v2-nav-item__icon :deep(.v-icon::before),
.v2-nav-item :deep(.v-icon) {
  color: currentColor !important;
  caret-color: currentColor;
}

.v2-nav-item--collapsed .v2-nav-item__icon {
  width: 24px;
  height: 24px;
}

.v2-nav-item__label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-nav-item__suffix {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

/* =========================================================
   NAVY VARIANT (sidebar on dark background)
   ========================================================= */
.v2-nav-item--navy {
  color: var(--color-navy-on-soft);
  padding: var(--space-3) var(--space-6);
  margin: 0;
  gap: var(--space-3);
  border-radius: 0;
  font-weight: var(--fw-medium);
}

.v2-nav-item--navy:hover {
  background-color: var(--color-navy-light);
  color: var(--color-amber);
}

.v2-nav-item--navy:hover .v2-nav-item__icon :deep(.v-icon) {
  transform: scale(1.10);
}

.v2-nav-item--navy.v2-nav-item--active {
  background-color: var(--color-navy-light);
  color: var(--color-amber);
  font-weight: var(--fw-bold);
  box-shadow: inset 4px 0 0 0 var(--color-amber);
}

.v2-nav-item--navy.v2-nav-item--active:hover {
  background-color: var(--color-navy-light);
  color: var(--color-amber);
}

.v2-nav-item--navy .v2-nav-item__icon :deep(.v-icon) {
  transition: transform var(--transition-base);
}

.v2-nav-item--navy.v2-nav-item--active .v2-nav-item__icon :deep(.v-icon) {
  font-variation-settings: "FILL" 1, "wght" 600, "GRAD" 0, "opsz" 24;
}

.v2-nav-item--navy.v2-nav-item--collapsed {
  padding: var(--space-3);
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
}

.v2-nav-item--navy.v2-nav-item--collapsed.v2-nav-item--active {
  box-shadow: none;
  position: relative;
}

.v2-nav-item--navy.v2-nav-item--collapsed.v2-nav-item--active::after {
  content: "";
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background-color: var(--color-amber);
  border-radius: var(--radius-full);
}

/* =========================================================
   NAVY DANGER VARIANT (logout)
   ========================================================= */
.v2-nav-item--navy-danger {
  color: var(--color-navy-on-soft);
  padding: var(--space-3) var(--space-6);
  margin: 0;
  gap: var(--space-3);
  border-radius: 0;
  font-weight: var(--fw-medium);
  background: transparent;
  border: 0;
  width: 100%;
  display: flex;
  align-items: center;
  text-align: left;
  cursor: pointer;
  transition: color var(--transition-fast), background-color var(--transition-fast);
}

.v2-nav-item--navy-danger:hover {
  color: var(--color-error);
  background-color: rgba(186, 26, 26, 0.10);
}
</style>
